import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarCheck2,
  CircleDollarSign,
  Clock3,
  FlagTriangleRight,
  Gauge,
  UsersRound,
} from "lucide-react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { StaffStatusAction } from "@/components/staff-status-action"
import { StatusChip } from "@/components/status-chip"
import { Button } from "@/components/ui/button"
import {
  addDays,
  formatCourseDate,
  formatCurrency,
  formatMinutes,
} from "@/lib/date"
import { staffOverviewQueryOptions } from "@/lib/query-options"

type DispatchSearch = { date?: string }

export const Route = createFileRoute("/staff/")({
  validateSearch: (search: Record<string, unknown>): DispatchSearch => ({
    date: typeof search.date === "string" ? search.date : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(staffOverviewQueryOptions(deps.date)),
  head: () => ({
    meta: [{ title: "Dispatch · Groundline staff" }],
  }),
  pendingComponent: () => <StaffPagePending kind="dashboard" />,
  component: StaffDispatch,
})

function StaffDispatch() {
  const search = Route.useSearch()
  const { data } = useSuspenseQuery(staffOverviewQueryOptions(search.date))
  const navigate = Route.useNavigate()

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <span className="size-2 rounded-full bg-accent ring-4 ring-accent/30" />
            Live dispatch
          </span>
          <h1 className="staff-page-title">Starter’s board.</h1>
          <p className="staff-page-copy">
            {formatCourseDate(data.date)} · {data.course.name}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="field min-w-[165px]">
            <label htmlFor="dispatch-date">Operating date</label>
            <input
              id="dispatch-date"
              className="control"
              type="date"
              min={data.today}
              max={addDays(data.today, 9)}
              value={data.date}
              onChange={(event) =>
                navigate({ search: { date: event.target.value } })
              }
            />
          </div>
          <Button
            render={<Link to="/staff/tee-sheet" search={{ date: data.date }} />}
            variant="outline"
          >
            Full tee sheet
            <ArrowRight data-icon="inline-end" />
          </Button>
        </div>
      </header>

      <section className="metric-grid" aria-label="Daily metrics">
        <Metric
          label="Active groups"
          value={data.metrics.activeBookings.toString()}
          icon={CalendarCheck2}
        />
        <Metric
          label="Players"
          value={data.metrics.players.toString()}
          icon={UsersRound}
        />
        <Metric
          label="Booked value"
          value={formatCurrency(data.metrics.revenue, data.course.currency)}
          icon={CircleDollarSign}
        />
        <Metric
          label="Day occupancy"
          value={`${data.metrics.occupancy}%`}
          icon={Gauge}
        />
      </section>

      <section className="ruled-panel mt-7 overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">
              <FlagTriangleRight className="size-3.5" />
              Today’s group tags
            </span>
            <h2 className="mt-1 text-sm font-semibold">
              Chronological dispatch queue
            </h2>
          </div>
          <span className="text-xs font-semibold text-muted-foreground">
            {data.bookings.length} total
          </span>
        </div>

        {data.bookings.length === 0 ? (
          <div className="empty-state">
            <div>
              <Clock3 className="size-6" />
              <strong>No groups booked</strong>
              <p className="mt-2">
                The rail is open. Public bookings will appear here immediately.
              </p>
            </div>
          </div>
        ) : (
          data.bookings.map((booking) => (
            <article
              key={booking.id}
              className="rule-row grid gap-4 p-4 sm:grid-cols-[92px_minmax(0,1fr)_auto] sm:items-center sm:px-5"
            >
              <div className="relative border-l-2 border-[#9eafa8] pl-5">
                <span
                  className="border-paper absolute top-1/2 left-[-6px] size-2.5 -translate-y-1/2 rounded-full border-2 bg-primary"
                  aria-hidden="true"
                />
                <strong className="block font-display text-[1.55rem] leading-none font-semibold">
                  {formatMinutes(booking.teeTime.startMinutes)}
                </strong>
                <small className="mt-1.5 block text-[0.66rem] font-bold tracking-[0.08em] text-muted-foreground uppercase">
                  {booking.teeTime.holes} holes
                </small>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-sm font-semibold">
                    {booking.leadPlayerName}
                  </h3>
                  <StatusChip status={booking.status} />
                </div>
                <p className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>{booking.reference}</span>
                  <span>
                    {booking.playerCount}{" "}
                    {booking.playerCount === 1 ? "player" : "players"}
                  </span>
                  <span className="capitalize">
                    {booking.cartPreference === "riding"
                      ? `${booking.cartCount} ${
                          booking.cartCount === 1 ? "cart" : "carts"
                        }`
                      : "walking"}
                  </span>
                  <span className="capitalize">
                    {booking.source.replace("_", " ")}
                  </span>
                </p>
                {booking.notes ? (
                  <p className="mt-2 max-w-[70ch] truncate text-[0.72rem] text-muted-foreground italic">
                    “{booking.notes}”
                  </p>
                ) : null}
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <strong className="text-xs">
                  {formatCurrency(booking.totalCents, booking.course.currency)}
                </strong>
                <StaffStatusAction
                  bookingId={booking.id}
                  status={booking.status}
                />
              </div>
            </article>
          ))
        )}
      </section>

      <div className="mt-4 flex justify-between gap-4 text-xs text-muted-foreground">
        <span>
          {data.metrics.blockedTimes} blocked{" "}
          {data.metrics.blockedTimes === 1 ? "time" : "times"}
        </span>
        <Link
          to="/staff/bookings"
          className="font-semibold text-primary hover:underline"
        >
          Review all bookings
        </Link>
      </div>
    </>
  )
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: typeof CalendarCheck2
}) {
  return (
    <div className="metric">
      <span className="metric__label">
        {label}
        <Icon />
      </span>
      <strong className="metric__value">{value}</strong>
    </div>
  )
}
